// src/componants/SellerHeader.tsx

import React from 'react'; // Removed useState as it's not strictly needed for hover logic
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext'; 
import logo from '../../assets/logo.png';

// Assuming useUser returns { user: { username: string, profileImg: string | null }, logout: () => void }

// --- Navigation Items Configuration ---
// Removed 'path' from items with a 'dropdown' to make them non-clickable.
const navItems = [
    { name: 'Dashboard', path: '/seller/dashboard' },
     { 
        name: 'Profile', 
        // path removed to make it non-clickable
        dropdown: [
            { name: 'User Profile', path: '/seller/profile/user' },
            { name: 'Business Profile', path: '/seller/profile/business' },
            { name: 'Bank Details', path: '/seller/profile/bank' },
            // Add any other profile-related links here
        ]
    },
     { 
        name: 'Leads & Inquiry' , path: '/seller/leads', 
        // path removed to make it non-clickable
        
    },
    { 
        name: 'Manage Products', 
        // path removed to make it non-clickable
        dropdown: [
            { name: 'My Products', path: '/seller/product' },
            { name: 'Add Product', path: '/seller/add-product' },
        ]
    },
    { name: 'Orders', path: '/seller/orders' },
   
    { name: 'Settings', path: '/seller/settings' }, // Replaced 'Payments' with 'Settings'
];
// --- End Navigation Items Configuration ---


// Reusable Dropdown Component for Cleanliness
interface DropdownItem {
    name: string;
    path: string;
}

interface NavItemType {
    name: string;
    path?: string; // path is now optional
    dropdown?: DropdownItem[];
}

interface NavItemProps {
    item: NavItemType;
    location: ReturnType<typeof useLocation>;
}

const NavItem: React.FC<NavItemProps> = ({ item, location }) => {
    // Determine if any dropdown item is currently active for styling the parent
    const isParentActive = item.dropdown 
        ? item.dropdown.some(d => location.pathname.startsWith(d.path))
        : location.pathname.startsWith(item.path || ''); // For simple links

    const baseClasses = 'h-full flex items-center font-medium transition duration-200 border-b-2 px-2';
    
    // If the item has a dropdown, it will be a hoverable menu (non-clickable parent)
    if (item.dropdown) {
        return (
            // Relative position for dropdown and group for hover effect
            <div 
                className={`relative h-full flex items-center group cursor-default font-medium transition duration-200 border-b-2 
                    ${isParentActive 
                        ? 'border-red-600 text-red-600 font-bold' // Active: Red Underline
                        : 'border-transparent text-gray-700 hover:border-red-600 hover:text-red-600' // Hover: Red Underline
                    }
                `}
            >
                {/* Render as a span (non-clickable text) for the parent item */}
                <span className="h-full flex items-center">
                    {item.name}
                </span>
                
                {/* Dropdown Menu Container */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 pt-2 z-50">
                    <div className={`
                        // Initial State: Hidden, slide-left, transparent
                        opacity-0 invisible scale-95 transform translate-x-[-10px]
                        
                        // Hover State: Visible, full opacity, slide-in from left
                        group-hover:opacity-100 group-hover:visible group-hover:scale-100 group-hover:translate-x-0
                        
                        // Transitions
                        transition-all duration-300 ease-out 
                        
                        // Styling
                        w-52 bg-white shadow-xl rounded-lg border border-gray-100 py-1
                    `}>
                        {item.dropdown.map((dropdownItem: DropdownItem) => (
                            <Link
                                key={dropdownItem.name}
                                to={dropdownItem.path}
                                // Highlight active dropdown item
                                className={`block px-4 py-2 text-sm transition duration-150 
                                    ${location.pathname.startsWith(dropdownItem.path)
                                        ? 'bg-red-50 text-red-600 font-semibold'
                                        : 'text-gray-700 hover:bg-gray-100 hover:text-red-600'
                                    }
                                `}
                            >
                                {dropdownItem.name}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Default Link (For Dashboard, Orders, Settings)
    return (
        <Link 
            key={item.name}
            to={item.path || ''} // Fallback for TypeScript, but simple links must have a path
            className={`${baseClasses} 
                ${isParentActive
                    ? 'border-red-600 text-red-600 font-bold' // Active: Red Underline
                    : 'border-transparent text-gray-700 hover:border-red-600 hover:text-red-600' // Hover: Red Underline
                }
            `}
        >
            {item.name}
        </Link>
    );
};


const SellerHeader: React.FC = () => {
    const { user, logout } = useUser();
    const navigate = useNavigate();
    const location = useLocation(); 

    // Dynamic Seller Name और Initial
    const sellerName = user?.username || "Seller";
    const userInitial = (sellerName && sellerName.length > 0
        ? sellerName.charAt(0)
        : 'S' // Default to 'S'
    ).toUpperCase();
    
    // Logout handler
    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        // Header height 64px (h-16)
        <header className="fixed top-0 left-0 w-full bg-white shadow-lg z-50 h-[64px] flex items-center px-4 sm:px-8 border-b border-gray-200">
            
            {/* 1. Left Section: Logo */}
            <a href="/" className="flex items-center flex-shrink-0">
      <img src={logo} alt="B2Bhaarat Logo" className="h-12 w-12 mr-2" />
      <span
        className="text-2xl font-extrabold text-red-600 "
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        B2Bhaarat
      </span>
    </a>
            {/* 2. Center Section: Navigation Links (Center Aligned) */}
            <nav className="flex-1 flex justify-center space-x-8 h-full">
                {navItems.map((item) => (
                    <NavItem key={item.name} item={item as NavItemType} location={location} />
                ))}
            </nav>

            {/* 3. Right Section: Profile Icon and Logout Button */}
            <div className="flex-shrink-0 flex items-center space-x-4">
                
                {/* Profile Avatar/Initial */}
                <div className="flex items-center space-x-2">
                    {/* Initial Avatar Logic (लाल बैकग्राउंड) */}
                    <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
                        {userInitial}
                    </div>
                    <span className="font-semibold text-gray-700 text-base hidden sm:inline-block">
                        {sellerName}
                    </span>
                </div>
                
                {/* Separator */}
                <div className="h-5 w-px bg-gray-300"></div>

                {/* Logout Button (साधारण Flipkart style) */}
                <button 
                    onClick={handleLogout}
                    className="flex items-center px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md transition duration-150 hover:bg-gray-100 hover:border-red-600 focus:outline-none"
                >
                    Logout
                </button>
            </div>
        </header>
    );
};

export default SellerHeader;